import { queryOptions } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { executeMutation, getRequestScope, queryClient, subscribeRequestScope } from './client';
import { prepareRequest, sendWithPolicy, RequestCancelledError } from './transport';
import type { RequestConfig } from './transport';
export { HttpError, isHttpError, RequestCancelledError, requestSettings } from './transport';
export type { RequestConfig, HttpResponse } from './transport';

let sequence = 0;
// Shared across request clients because writes invalidate all HTTP reads in the scope.
let writeRevision = 0;
const signalIds = new WeakMap<AbortSignal, number>();
const headerIds = new Map<string, number>();
function headerId(headers: unknown) {
    const key = JSON.stringify(headers);
    if (!headerIds.has(key)) headerIds.set(key, ++sequence);
    return headerIds.get(key)!;
}
// These GETs perform actions or consume tokens. Execute them once as mutations:
// they must never enter the query cache or its automatic freshness replay loop.
export const isActionGet = (url: string) => /\/(?:logout|csrf-cookie|realtime\/token|jitsi\/token|reports\/run|places\/(?:search|details|distance|reverse-geocode))(?:[/?#]|$)/.test(url);

export function createRequests(defaults: Partial<RequestConfig> = {}) {
    const id = ++sequence;
    const prepare = (input: RequestConfig) => prepareRequest({ ...defaults, ...input, headers: { ...defaults.headers, ...input.headers } });
    async function run<T>(request: RequestConfig, scope: number): Promise<T> {
        if (scope !== getRequestScope()) throw new RequestCancelledError('Request context changed');
        if (request.csrf !== false && !['GET', 'HEAD', 'OPTIONS'].includes(request.method!) && !Cookies.get('XSRF-TOKEN')) {
            await queryClient.fetchQuery({
                queryKey: ['csrf', scope, request.baseURL], gcTime: 0, retry: false,
                queryFn: ({ signal }) => sendWithPolicy({ ...request, url: '/sanctum/csrf-cookie', method: 'GET', data: undefined, params: undefined, signal, csrf: false }),
            });
        }
        if (scope !== getRequestScope()) throw new RequestCancelledError('Request context changed');
        const result = await sendWithPolicy<T>(request);
        if (scope !== getRequestScope()) throw new RequestCancelledError('Request context changed');
        return result;
    }
    function options<T = any>(input: RequestConfig) {
        if (isActionGet(input.url)) throw new Error('Action endpoints require a mutation');
        const request = prepare(input);
        const scope = getRequestScope();
        if (request.signal && !signalIds.has(request.signal)) signalIds.set(request.signal, ++sequence);
        return queryOptions<T, unknown>({
            queryKey: ['http', scope, id, request.method, request.url, {
                params: request.params instanceof URLSearchParams ? request.params.toString() : request.params,
                data: request.data, baseURL: request.baseURL, responseType: request.responseType,
                credentials: request.credentials, unwrap: request.unwrap, handleErrors: request.handleErrors, timeout: request.timeout, headerId: headerId(request.headers),
                callerSignal: request.signal ? signalIds.get(request.signal) : undefined,
            }],
            queryFn: async ({ signal }) => {
                const controller = new AbortController();
                const abort = () => controller.abort();
                signal.addEventListener('abort', abort, { once: true });
                request.signal?.addEventListener('abort', abort, { once: true });
                if (signal.aborted || request.signal?.aborted) abort();
                try {
                    // Invalidation alone can join an initial pending fetch. Keep its
                    // shared promise, but never publish a snapshot from before a write.
                    while (true) {
                        if (controller.signal.aborted) throw new RequestCancelledError('Request cancelled');
                        const revision = writeRevision;
                        const result = await run<T>({ ...request, signal: controller.signal }, scope);
                        if (controller.signal.aborted) throw new RequestCancelledError('Request cancelled');
                        if (revision === writeRevision) return result;
                    }
                }
                finally { signal.removeEventListener('abort', abort); request.signal?.removeEventListener('abort', abort); }
            },
            staleTime: input.query?.staleTime ?? 0,
            gcTime: input.query?.gcTime ?? (input.responseType === 'blob' || input.responseType === 'arraybuffer' ? 0 : 300_000),
            retry: false, structuralSharing: false,
        });
    }
    function mutate<T = any, R = T>(input: RequestConfig): Promise<R> {
        const request = prepare(input);
        const scope = getRequestScope();
        return executeMutation(['http', scope, id, request.method, request.url], async () => {
            const controller = new AbortController();
            const abort = () => controller.abort();
            const unsubscribe = subscribeRequestScope(abort);
            request.signal?.addEventListener('abort', abort, { once: true });
            if (request.signal?.aborted || scope !== getRequestScope()) abort();
            try {
                const result = await run<R>({ ...request, signal: controller.signal }, scope);
                if (!isActionGet(request.url)) {
                    writeRevision++;
                    // Pending reads handle the revision above, including initial loads
                    // without cached data. Do not cancel their existing consumers.
                    void queryClient.invalidateQueries({ queryKey: ['http', scope], refetchType: 'active' }, { cancelRefetch: false });
                }
                return result;
            } finally {
                unsubscribe(); request.signal?.removeEventListener('abort', abort);
            }
        });
    }
    return {
        query: <T = any, R = T>(input: RequestConfig): Promise<R> => isActionGet(input.url) ? mutate<T, R>(input) : queryClient.fetchQuery(options<R>(input)),
        mutate,
        queryOptions: options,
    };
}
const requests = createRequests();
export default requests;
